/**
 * DATABASE HELPERS
 * 
 * This file contains helper functions for interacting with Firestore.
 * Firestore is a NoSQL database - it stores data in "collections" and "documents".
 * 
 * Think of it like:
 * - Collections = folders
 * - Documents = files in those folders
 * 
 * Structure:
 * - users/{userId} - User profiles
 * - users/{userId}/items/{itemId} - Collection items (polishes, tools, etc.) ← UPDATED!
 * - projects/{projectId} - Nail art projects
 * - listings/{listingId} - Marketplace listings
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import type { CollectionItem, Project, User } from '../types';
import { slugify } from '../utils/polishSlug';

/** Safely convert a Firestore Timestamp, Date, string, or number to a JS Date. */
function safeToDate(value: unknown): Date {
  if (!value) return new Date();
  if (typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return new Date(value as string | number);
}

// ============================================================================
// COLLECTION ITEMS (Polishes, Tools, Plates, Embellishments)
// ============================================================================

/**
 * Add a new item to a user's collection
 * 
 * @param userId - The user who owns this item
 * @param itemData - The item data (without id, that's auto-generated)
 * @returns The ID of the newly created item
 * 
 * EXAMPLE USAGE:
 * const newItem = {
 *   userId: 'user123',
 *   itemType: 'polish',
 *   brand: 'Alchemy Lacquers',
 *   name: 'Begonya',
 *   quantity: 1,
 *   colorDescription: 'micro glitter polish',
 *   finishTypes: ['holo', 'glitter', 'shimmer'],
 *   hasSwatched: true,
 *   swatchPhotos: [],
 *   notes: '',
 *   dateAdded: new Date(),
 *   isForSale: false,
 *   tags: ['holo', 'glitter'],
 *   favorited: false,
 *   usedInProjects: []
 * };
 * 
 * const itemId = await addItem('user123', newItem);
 */
export const addItem = async (userId: string, itemData: Omit<CollectionItem, 'id'>) => {
  try {
    // UPDATED: Items now nested under users
    const itemsCollection = collection(db, 'users', userId, 'items');
    
    // Convert Date objects to Firestore Timestamps
    const dataWithTimestamp = {
      ...itemData,
      dateAdded: Timestamp.fromDate(itemData.dateAdded),
      brandLower: (itemData.brand ?? '').toLowerCase(),
      nameLower: (itemData.name ?? '').toLowerCase(),
      // Universal slugified name for URL construction. Written on every
      // new item so plate/oil/etc. detail pages can look up the item via
      // its URL segment without needing to reverse-slugify at query time.
      brandSlug: slugify(itemData.brand ?? ''),
      nameSlug: slugify(itemData.name ?? ''),
    };

    // Add the document and get its reference
    const docRef = await addDoc(itemsCollection, dataWithTimestamp);
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding item:', error);
    throw error;
  }
};

/**
 * Get all items for a specific user
 * 
 * @param userId - The user whose items to fetch
 * @returns Array of collection items
 * 
 * EXAMPLE USAGE:
 * const myItems = await getUserItems('user123');
 * console.log(`You have ${myItems.length} items`);
 */
export const getUserItems = async (userId: string): Promise<CollectionItem[]> => {
  try {
    // UPDATED: Query from nested collection
    const itemsRef = collection(db, 'users', userId, 'items');
    
    // No need for where clause anymore - we're already in the user's items
    const snapshot = await getDocs(itemsRef);
    
    const items: CollectionItem[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      items.push({
        id: doc.id,
        ...data,
        // Convert Firestore Timestamp back to Date
        dateAdded: safeToDate(data.dateAdded),
      } as CollectionItem);
    });
    
    return items;
  } catch (error) {
    console.error('Error getting user items:', error);
    return [];
  }
};

/**
 * Get a single item by ID
 * 
 * @param userId - The user who owns the item
 * @param itemId - The item ID
 * @returns The item, or null if not found
 */
export const getItem = async (userId: string, itemId: string): Promise<CollectionItem | null> => {
  try {
    // UPDATED: Path includes userId
    const itemDoc = doc(db, 'users', userId, 'items', itemId);
    const docSnap = await getDoc(itemDoc);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        dateAdded: safeToDate(data.dateAdded),
      } as CollectionItem;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting item:', error);
    throw error;
  }
};

/**
 * Update an existing item
 * 
 * @param userId - The user who owns the item
 * @param itemId - The item ID
 * @param updates - Object with fields to update
 * 
 * EXAMPLE USAGE:
 * await updateItem('user123', 'item123', {
 *   quantity: 2,
 *   notes: 'Bought another bottle!'
 * });
 */
export const updateItem = async (userId: string, itemId: string, updates: Partial<CollectionItem>) => {
  try {
    // UPDATED: Path includes userId
    const itemDoc = doc(db, 'users', userId, 'items', itemId);
    await updateDoc(itemDoc, {
      ...updates,
      ...(updates.brand !== undefined && {
        brandLower: updates.brand.toLowerCase(),
        brandSlug: slugify(updates.brand),
      }),
      ...(updates.name !== undefined && {
        nameLower: updates.name.toLowerCase(),
        nameSlug: slugify(updates.name),
      }),
    });
  } catch (error) {
    console.error('Error updating item:', error);
    throw error;
  }
};

/**
 * Delete an item
 * 
 * @param userId - The user who owns the item
 * @param itemId - The item ID
 */
export const deleteItem = async (userId: string, itemId: string) => {
  try {
    // UPDATED: Path includes userId
    const itemDoc = doc(db, 'users', userId, 'items', itemId);
    await deleteDoc(itemDoc);
  } catch (error) {
    console.error('Error deleting item:', error);
    throw error;
  }
};

/**
 * Search items by brand
 * 
 * @param userId - The user whose items to search
 * @param brandName - The brand to search for
 * @returns Array of matching items
 * 
 * EXAMPLE USAGE:
 * const alchemyPolishes = await searchItemsByBrand('user123', 'Alchemy Lacquers');
 */
export const searchItemsByBrand = async (userId: string, brandName: string): Promise<CollectionItem[]> => {
  try {
    // UPDATED: Query from nested collection
    const itemsCollection = collection(db, 'users', userId, 'items');
    
    // Query: items where brand matches (no need for userId check)
    const q = query(
      itemsCollection, 
      where('brand', '==', brandName)
    );
    
    const querySnapshot = await getDocs(q);
    
    const items: CollectionItem[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      items.push({
        id: doc.id,
        ...data,
        dateAdded: safeToDate(data.dateAdded),
      } as CollectionItem);
    });
    
    return items;
  } catch (error) {
    console.error('Error searching items:', error);
    throw error;
  }
};

/**
 * Search items by tags
 * 
 * @param userId - The user whose items to search
 * @param tag - The tag to search for (e.g., "holo", "glitter")
 * @returns Array of matching items
 * 
 * EXAMPLE USAGE:
 * const holoPolishes = await searchItemsByTag('user123', 'holo');
 */
export const searchItemsByTag = async (userId: string, tag: string): Promise<CollectionItem[]> => {
  try {
    // UPDATED: Query from nested collection
    const itemsCollection = collection(db, 'users', userId, 'items');
    
    // Query: tags array contains this tag (no need for userId check)
    const q = query(
      itemsCollection,
      where('tags', 'array-contains', tag)
    );
    
    const querySnapshot = await getDocs(q);
    
    const items: CollectionItem[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      items.push({
        id: doc.id,
        ...data,
        dateAdded: safeToDate(data.dateAdded),
      } as CollectionItem);
    });
    
    return items;
  } catch (error) {
    console.error('Error searching items by tag:', error);
    throw error;
  }
};

// ============================================================================
// PROJECTS
// ============================================================================

/**
 * Create a new project
 * 
 * @param projectData - The project data
 * @returns The ID of the newly created project
 */
export const createProject = async (projectData: Omit<Project, 'id'>) => {
  try {
    const projectsCollection = collection(db, 'projects');
    
    const dataWithTimestamp = {
      ...projectData,
      dateCreated: Timestamp.fromDate(projectData.dateCreated)
    };
    
    const docRef = await addDoc(projectsCollection, dataWithTimestamp);
    return docRef.id;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

/**
 * Get all projects for a user
 * 
 * @param userId - The user whose projects to fetch
 * @returns Array of projects
 */
export const getUserProjects = async (userId: string): Promise<Project[]> => {
  try {
    const projectsCollection = collection(db, 'projects');
    const q = query(projectsCollection, where('userId', '==', userId));
    
    const querySnapshot = await getDocs(q);
    
    const projects: Project[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      projects.push({
        id: doc.id,
        ...data,
        dateCreated: data.dateCreated.toDate(),
      } as Project);
    });
    
    return projects;
  } catch (error) {
    console.error('Error getting user projects:', error);
    throw error;
  }
};

/**
 * Get public projects (for browsing/discovery)
 * 
 * @param limitCount - How many projects to fetch
 * @returns Array of public projects
 */
export const getPublicProjects = async (limitCount: number = 20): Promise<Project[]> => {
  try {
    const projectsCollection = collection(db, 'projects');
    
    // Query: get public projects, ordered by date (newest first), limited to count
    const q = query(
      projectsCollection,
      where('isPublic', '==', true),
      orderBy('dateCreated', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    const projects: Project[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      projects.push({
        id: doc.id,
        ...data,
        dateCreated: data.dateCreated.toDate(),
      } as Project);
    });
    
    return projects;
  } catch (error) {
    console.error('Error getting public projects:', error);
    throw error;
  }
};

// ============================================================================
// USERS
// ============================================================================

/**
 * Create a user profile
 * This should be called after a user signs up with Firebase Auth
 * 
 * @param userId - The Firebase Auth UID
 * @param userData - The user profile data
 */
export const createUserProfile = async (userId: string, userData: Omit<User, 'id'>) => {
  try {
    // Use the Firebase Auth UID as the document ID
    const userDoc = doc(db, 'users', userId);
    
    const dataWithTimestamp = {
      ...userData,
      joinDate: Timestamp.fromDate(userData.joinDate)
    };
    
    // Set the document (creates it if doesn't exist)
    await updateDoc(userDoc, dataWithTimestamp);
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Get a user profile
 * 
 * @param userId - The user ID
 * @returns The user profile, or null if not found
 */
export const getUserProfile = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = doc(db, 'users', userId);
    const docSnap = await getDoc(userDoc);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        joinDate: data.joinDate.toDate(),
      } as User;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};
